/**
 * CardDragHandle - the drag grip that a DraggableCardGrid renders INSIDE each
 * card's header (as the first item, pushing the card's own icon/title right,
 * like the dashboard tiles).
 *
 * A card component drops `<CardDragHandle />` at the start of its header. The
 * handle renders the dot-grid grip ONLY when the card is inside a
 * DraggableCardGrid (which wraps every card in `CardDragHandleProvider`);
 * anywhere else it renders nothing, so non-reorderable usages are untouched.
 *
 * The grip is faded at rest (opacity-40) and goes full strength when its card is
 * hovered/focused (`group-hover/reorder`, the group set on the grid's card
 * wrapper). The whole card stays draggable - the grid listens for pointerdown on
 * the card wrapper - so this is purely the affordance + a touch grab target.
 *
 * WPS-166: the same slot also carries the card's HIDE control when the grid is
 * hideable. It sits beside the grip so the two card-level affordances live in
 * one place, and it is a real button (the shared `Button`) - the grid's
 * pointerdown handler skips buttons, so pressing it never starts a drag.
 *
 * Lives in `shared/` so both admin card primitives and shared cards
 * (EntityHistoryCard) can render the slot without an admin -> shared dependency.
 */

import { createContext, useContext, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EyeOff } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

type CardControlsValue = {
  /** True when the card is inside a DraggableCardGrid (drag affordance shown). */
  inGrid: boolean
  /** Hide this card, when the grid supports hiding. Undefined = not hideable. */
  onHide?: () => void
  /** Card title, used for the hide control's accessible label. */
  title?: string
}

const CardDragHandleContext = createContext<CardControlsValue>({ inGrid: false })

export function CardDragHandleProvider({
  children,
  onHide,
  title,
  locked = false,
}: {
  children: ReactNode
  onHide?: () => void
  title?: string
  /** Pinned: no grip and no hide control. The provider still renders, so
   *  pinning keeps the card mounted (and an open edit dialog open). */
  locked?: boolean
}) {
  const value = locked ? { inGrid: false } : { inGrid: true, onHide, title }
  return (
    <CardDragHandleContext.Provider value={value}>
      {children}
    </CardDragHandleContext.Provider>
  )
}

export function CardDragHandle({ className }: { className?: string }) {
  const { inGrid, onHide, title } = useContext(CardDragHandleContext)
  const { t } = useTranslation()
  if (!inGrid) return null
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span
        title={t('detailCards.dragToReorder', 'Drag to reorder this card')}
        className={cn(
          'hidden shrink-0 cursor-grab touch-none text-[var(--ink-3)] opacity-40 transition-opacity duration-150 hover:text-[var(--green)] group-hover/reorder:opacity-100 md:block',
          className,
        )}
      >
        <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true" focusable="false">
          <circle cx="3" cy="2" r="1.1" />
          <circle cx="7" cy="2" r="1.1" />
          <circle cx="3" cy="6" r="1.1" />
          <circle cx="7" cy="6" r="1.1" />
          <circle cx="3" cy="10" r="1.1" />
          <circle cx="7" cy="10" r="1.1" />
        </svg>
      </span>
      {onHide ? (
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onHide}
          aria-label={
            title
              ? t('detailCards.hideNamedCard', 'Hide the {{title}} card', { title })
              : t('detailCards.hideCard', 'Hide this card')
          }
          title={t('detailCards.hideCard', 'Hide this card')}
          // Sized to the grip beside it, not the icon-xs default.
          className="hidden size-auto shrink-0 cursor-pointer rounded-[4px] p-0.5 text-[var(--ink-3)] opacity-0 transition-opacity duration-150 hover:bg-transparent hover:text-[var(--green)] dark:hover:bg-transparent focus-visible:opacity-100 group-hover/reorder:opacity-100 md:inline-flex"
        >
          <EyeOff className="h-3 w-3" />
        </Button>
      ) : null}
    </span>
  )
}
